import { useAppDispatch } from 'app/hooks';
import classNames from 'classnames';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import FormattedDate from 'components/formatters/FormattedDate';
import FormattedMessage from 'components/formatters/FormattedMessage';
import { useConfirmDelete } from 'components/modal/forms/ConfirmDeleteForm';
import DropMenu from 'components/navigation/DropMenu';
import {
  SortableItem,
  SortableItemType,
  SortableListInfo,
} from 'components/sortables/Sortable';
import { IFile } from 'features/files/fileAPI';
import {
  deleteFileAsync,
  getFileTypeInfo,
  patchFileAsync,
} from 'features/files/fileSlice';
import { IS_DEV } from 'helpers/consts';
import { fileSize } from 'helpers/helpers';
import { AnyIdObject } from 'helpers/objects';
import { useUser } from 'selectors/userSelectors';
import { useModal } from '../modal/ModalContext';

import TextAreaInput from 'components/forms/TextAreaInput';
import Icon from 'components/icons/Icon';
import { IconSize, IconSymbol, IconVariant } from 'components/icons/Icon.types';
import { useSortablePreview } from 'components/sortables/SortableContext';
import { ImageViewLink } from './ImageViewLink';
import { getLibraryPathForFile } from './LibraryFileViewer';

type LibraryFileProps = {
  file: IFile;
  listInfo: SortableListInfo;
  inRoot?: boolean;
};

export default function LibraryFile(
  props: LibraryFileProps & PropsWithChildren,
) {
  const { file, listInfo, inRoot, children } = props;
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const { user } = useUser();
  const isPreview = useSortablePreview();

  const [editing, setEditing] = useState<boolean>(false);
  const [description, setDescription] = useState<string>('');
  const itemRef = useRef<HTMLDivElement>(null!);
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [_, setVersion] = useState<number>(0);
  const { openModal } = useModal();
  const confirmDelete = useConfirmDelete();

  const forceRender = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    setDescription(file.description || '');
  }, [file]);

  function handleRenameFile() {
    const s = description;
    if (file.uri)
      // renaming a file
      dispatch(
        patchFileAsync({
          data: {
            ...file,
            description: s,
          },
        }),
      ).then((response: any) => {
        switch (response.payload?.response.status) {
          case 200:
            break;
          default:
        }
        setEditing(false);
        setDescription(file.description || '');
      });
  }

  function handleDescriptionEdit() {
    file.description && setDescription(file.description);
    setEditing(true);
  }

  function handleCopyPath() {
    const filePath = getLibraryPathForFile(file);
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(filePath);
      return;
    }
    const input = document.createElement('input');
    input.value = filePath;
    input.setAttribute('readonly', 'true');
    input.style.position = 'absolute';
    input.style.left = '-9999px';
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  }

  function handleFileDelete() {
    async function doDelete() {
      if (file.id) {
        if (file.uri) {
          // delete the file
          dispatch(
            deleteFileAsync({
              file: file,
            }),
          ).then((response: any) => {
            switch (response.payload?.response.status) {
              case 204:
                break;
              case 500:
              default:
                console.log(response);
            }
          });
        }
      }
    }
    confirmDelete({
      prompts: {
        prompt1: intl.formatMessage({ id: 'GENERAL.DELETE.FILE.CNF' }),
        prompt2: file.description,
      },
    }).then((confirmed: boolean) => {
      if (confirmed) doDelete();
    });
  }

  function handleDescriptionChange(input: string, hasText: boolean) {
    setDescription(input);
  }

  function handleBlur() {
    setEditing(false);
  }

  return (
    <SortableItem
      listInfo={listInfo}
      item={file as AnyIdObject}
      itemType={SortableItemType.File}
      accepting={[SortableItemType.None]}
      noDropIndicator={true}
      style={{ height: isPreview ? '35px' : undefined }}
      className={classNames({
        inRoot: inRoot,
      })}
      disabled={!user}
    >
      <div
        ref={itemRef}
        className={classNames('file', {})}
        data-file={IS_DEV ? file.id : undefined}
      >
        <div className="files_icon">
          <Icon symbol={getFileTypeInfo(file).icon} size={24} />
        </div>
        <div className={classNames('files_description', { editing })}>
          {editing ? (
            <TextAreaInput
              // asInput={true}
              onChange={handleDescriptionChange}
              onBlur={handleBlur}
              onSubmit={handleRenameFile}
              value={description}
            />
          ) : file.uri ? (
            // ImageViewLink component for preview
            <ImageViewLink className="files_link" file={file} noPreview={true}>
              {file.description}
            </ImageViewLink>
          ) : (
            <>{file.description}</>
          )}
        </div>
        <div className="files_size">{file.size && fileSize(file.size)}</div>
        {user ? (
          <div className="msg_menu">
            <DropMenu containerTailHeight={57}>
              <DropMenu.Trigger>
                <Icon
                  symbol={IconSymbol.ellipsis}
                  className="ellipsis"
                  hoverVariant={IconVariant.dark}
                  size={IconSize.tiny}
                />
                <div className="files_date">
                  {file.uploadTime ? (
                    <FormattedDate
                      value={file.uploadTime}
                      showYear={true}
                      format={{ year: '2-digit' }}
                    />
                  ) : (
                    ''
                  )}
                </div>
              </DropMenu.Trigger>
              <DropMenu.Items>
                {user ? (
                  <div className="menu_item" onClick={handleDescriptionEdit}>
                    <FormattedMessage id="FILES.EDIT" />
                    <Icon symbol={IconSymbol.pencil} size={22} />
                  </div>
                ) : undefined}
                {user ? (
                  <div className="menu_item" onClick={handleCopyPath}>
                    <FormattedMessage id="FILES.COPY_PATH" />
                    <Icon symbol={IconSymbol.copy} size={22} />
                  </div>
                ) : undefined}
                {user ? (
                  <div className="menu_item" onClick={handleFileDelete}>
                    <FormattedMessage id="X.DELETE" />
                    <Icon symbol={IconSymbol.bin} size={22} />
                  </div>
                ) : undefined}
                {/* {user && isSummaryType(file.mimeType ?? '') ? (
                  <>
                    <DropMenu.Divider />
                    <div
                      className="menu_item"
                      onClick={handleSummaryGeneration}
                    >
                      <FormattedMessage
                        id={
                          summaryRef.current
                            ? 'LIBRARY.SUMMARY.EDIT'
                            : 'LIBRARY.SUMMARY.GENERATE'
                        }
                      />
                    </div>
                    {summaryRef.current ? (
                      <div
                        className="menu_item"
                        onClick={() => handleDeleteSummary()}
                      >
                        <FormattedMessage id={'LIBRARY.SUMMARY.DELETE'} />
                      </div>
                    ) : null}
                  </>
                ) : undefined} */}
              </DropMenu.Items>
            </DropMenu>
          </div>
        ) : (
          <div className="files_date">
            {file.uploadTime ? (
              <FormattedDate value={file.uploadTime} showYear />
            ) : (
              ''
            )}
          </div>
        )}
        {children}
      </div>
      {/* {summaryRef.current?.content ? (
        <PopUp
          className="summaryPopup"
          show={showSummary}
          parentRef={itemRef}
          right
          lift={8}
          setShow={setShowSummary}
        >
          <div className="header">{file.description}</div>
          <div className="summary_header">
            <FormattedMessage id="LIBRARY.SUMMARY.SUMMARY" />
          </div>
          <div className="summary_note">
            <FormattedMessage id="LIBRARY.SUMMARY.DISCLAIMER" />
          </div>
          <MarkdownContent escapedLineBreaks={true}>
            {summaryRef.current?.content ??
              intl.formatMessage({ id: 'LIBRARY.SUMMARY.UNAVAILABLE' })}
          </MarkdownContent>
        </PopUp>
      ) : null} */}
    </SortableItem>
  );
}
